import React, { useState, useEffect, useMemo } from 'react'
import { Button } from '../../Components/UI/button'
import { Input } from '../../Components/UI/input'
import { Pencil, ArrowUp, ArrowDown, Check, X, Calendar, Car, Search, Plus, Loader2, User, ChevronUp, ChevronDown, Filter, ArrowLeft } from 'lucide-react'
import { CommonFilter } from '../../Components/Common/CommonFilter'
import { CommonDropdownFilter } from '../../Components/Common/CommonDropdownFilter'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../Components/UI/table'
import { Card, CardContent } from '../../Components/UI/card'
import { cn } from "../../Lib/Cn";
import { TooltipProvider } from '../../Components/UI/tooltip'
import { getTableDateOrWeekday } from '../../Utils/DateTime'
import { useCurrency } from '../../Hooks/useCurrency'
import { FormatAmount, getLast36MonthsOptions, getOtherMonthOptions, monthMap } from '../../Utils/Utilities'
import { getExpensesAPI, setExpensesData } from "./ExpensesAPI";
import { toast } from 'sonner'
import { parse, getMonth, format, set } from "date-fns";
import useIsMobile from '../../Hooks/useIsMobile'
import ExpenseEntry from './ExpenseEntry'
import { checkAPIResponse } from '../../Utils/APIResponseUtils'
import { useDispatch, useSelector } from 'react-redux'
import { getExpensesRefreshTrigger, getUsersArr, isSmartSellerMode } from '../../Store/Reducers'
import DrawerFilter from '../../Components/Common/DrawerFilter';
import { Drawer, DrawerClose, DrawerContent, DrawerFooter } from '../../Components/UI/drawer';
import { RiResetLeftFill } from 'react-icons/ri'
import { setGlobalLoading } from '../../Store/Actions'

const Expenses = () => {
  const isMobile = useIsMobile();
  const [selectedFilters, setSelectedFilters] = useState(() => {
    const now = new Date();
    return {
      monthFilter: 'thisMonth',
      month: (now.getMonth() + 1).toString(),
      year: now.getFullYear().toString(),
      otherMonth: null,
      category: 'all',
      executive: 'all'
    };
  });
  const currencySymbol = useCurrency();
  const [expenses, setExpenses] = useState([]);
  const [editingExpense, setEditingExpense] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null })
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterSelected, setIsFilterSelected] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const usersFromRedux = useSelector(getUsersArr);
  const expensesRefreshTrigger = useSelector(getExpensesRefreshTrigger);
  const dispatch = useDispatch();

  const expenseTypesArray = [
    { name: 'Awareness / Marketing', ID: 1 },
    { name: 'New Conversions', ID: 2 },
    { name: 'Order / Delivery', ID: 4 },
    { name: 'Repeat Business', ID: 3 },
  ];

  // Handle edit button click
  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setIsEditDialogOpen(true);
  };

  //Fetch Data for month
  const fetchDataForMonth = async (year, month) => {
    const params = {
      prdType: "MM",
      prdQty: "1",
      year,
      month
    };
    const data = await getExpensesAPI(params);

    const safeParse = s => { try { return JSON.parse(s || "[]") } catch { return [] } };
    const dailyExpenses = safeParse(data[0].DailyExpenses);

    const expenseData = dailyExpenses.map((item) => {
      const EID = parseInt(item.EID);
      const D = parseInt(item.D);
      const ETID = parseInt(item.ETID);
      const E = parseFloat(item.E);
      const Dsc = item.Dsc || '';
      const UID = parseInt(item.UID);

      return {
        expenseID: EID,                     // EID → expenseID
        expenseDT: new Date(year, month - 1, D), // D → date in current year+month
        expenseTypeID: ETID,                // ETID → expenseTypeID
        amount: E,                          // E → amount
        notes: Dsc,                         // Dsc → notes
        tags: '',
        userID: UID
      };
    });

    setExpenses(expenseData);
  };

  // Get executive name by userID
  const getExecutiveName = (userID) => {
    if (!userID) return '-';
    return usersFromRedux.find(u => u.userID === userID)?.name || '-';
  };

  // Initial data fetch
  useEffect(() => {
    (async () => {
      dispatch(setGlobalLoading(true));
      const now = new Date();
      await fetchDataForMonth(now.getFullYear(), now.getMonth() + 1);
      dispatch(setGlobalLoading(false));
    })();
  }, []);

  useEffect(() => {
    if (expensesRefreshTrigger > 0) {
      fetchDataForMonth(
        parseInt(selectedFilters.year),
        parseInt(selectedFilters.month)
      );
    }
  }, [expensesRefreshTrigger, selectedFilters.year, selectedFilters.month]);


  // Filtered Expenses based on selected filters and search term
  const filteredExpenses = useMemo(() => {
    let filtered = expenses;

    if (selectedFilters.category !== 'all') {
      filtered = filtered.filter(
        expense => expense.expenseTypeID === selectedFilters.category
      );
    }

    if (selectedFilters.executive !== 'all') {
      if (selectedFilters.executive === 'unassigned') {
        filtered = filtered.filter(expense =>
          !expense.userID || expense.userID === null
        );
      } else {
        filtered = filtered.filter(
          expense => expense.userID === selectedFilters.executive
        );
      }
    }

    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(expense => {
        const matchesSearch = [
          expenseTypesArray.find(type => type.ID === expense.expenseTypeID)?.name || '',
          getExecutiveName(expense.userID) || '',
          expense.notes || '',
        ].some(field => field.toLowerCase().includes(term));
        return matchesSearch;
      });
    }

    return filtered;
  }, [
    expenses,
    selectedFilters.category,
    selectedFilters.executive,
    searchTerm,
    expenseTypesArray
  ]);

  // Table Header Columns
  const tableHeaderCols = useMemo(() => {
    const baseColumns = [
      { key: 'date', label: 'Date', className: 'align-middle' },
      { key: 'category', label: 'Category', className: 'align-middle' },
      {
        key: 'amount',
        label: (
          <span className="flex items-center gap-1">
            Amount
            <span className="whitespace-nowrap text-gray-500 text-base">
              ({currencySymbol})
            </span>
          </span>
        ),
        className: 'align-middle text-right'
      },
    ];

    const additionalColumns = [];

    if (selectedFilters.executive === 'all') {
      additionalColumns.push({
        key: 'uID',
        label: 'Executive',
        className: 'align-middle'
      });
    }

    additionalColumns.push({
      key: 'description',
      label: 'Note',
      className: 'align-middle'
    });

    return [
      ...baseColumns,
      ...additionalColumns,
    ];
  }, [selectedFilters.executive]);

  // Filter button
  const FilterButton = () => {
    let activeFilterClass = isFilterSelected && !isMobile ? "filter-active" : "";
    let filterIconToggleClass = "h-4 w-4 " + (isMobile ? "" : (isFilterSelected ? "text-sahi-primary-color" : "text-white group-hover:text-sahi-primary-color"));
    return (
      <Button
        className={cn("button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px]", activeFilterClass)}
        onClick={() => {
          if (isMobile) {
            setIsDrawerOpen(true);
            setIsFilterSelected(true);
          } else {
            setIsFilterSelected(!isFilterSelected);
          }
        }} aria-label="Toggle Filter"
      >
        <div className="h-6 w-6 flex items-center justify-center transition-all">
          <Filter className={cn(filterIconToggleClass)} />
        </div>
      </Button>
    );
  }

  // Filter configuration
  const filterOptions = [
    {
      key: "monthFilter",
      label: "Period",
      options: [
        { name: 'This Month', ID: 'thisMonth' },
        { name: 'Last Month', ID: 'lastMonth' },
        { name: 'Other Month', ID: 'other' }
      ],
      displayValue: (val) => {
        const option = [
          { name: 'This Month', ID: 'thisMonth' },
          { name: 'Last Month', ID: 'lastMonth' },
          { name: 'Other Month', ID: 'other' }
        ].find(o => o.ID === val);
        return option?.name || 'This Month';
      },
      type: 'list',
      searchEnabled: false,
      showClearButton: false,
      hideAllOption: true,
      mobileDropdownText: "Period"
    },
    {
      key: "otherMonth",
      label: "Select Month",
      options: getLast36MonthsOptions(),
      disabled: selectedFilters.monthFilter !== 'other',
      displayValue: (val) => val || 'Select Month',
      type: 'list',
      searchEnabled: true,
      showClearButton: false,
      hideAllOption: true,
      mobileDropdownText: "Select Month"
    },
    {
      key: "category",
      label: "Category",
      options: [{ name: 'All', ID: 'all' }, ...expenseTypesArray.map(c => ({
        name: c.name,
        ID: c.ID
      }))],
      displayValue: (val) => {
        if (val === 'all') return 'All';
        return expenseTypesArray.find(c => c.ID === val)?.name || 'All';
      },
      type: 'list',
      searchEnabled: true,
      placeholder: "All",
      mobileDropdownText: "Select Category",
      hideAllOption: true
    },
    ...(!isSmartSellerMode() ? [{
      key: "executive",
      label: "Executive",
      options: [
        { name: 'All', ID: 'all' },
        ...usersFromRedux.map(u => ({ name: u.name, ID: u.userID }))
      ],
      displayValue: (val) => {
        if (val === 'all') return 'All';
        return usersFromRedux.find(u => u.userID === val)?.name || 'All';
      },
      type: 'list',
      searchEnabled: true,
      placeholder: "All",
      mobileDropdownText: "Select Executive",
      hideAllOption: true
    }] : []),

  ]

  // Function to reset filters
  const handleResetFilters = () => {
    const now = new Date();
    const resetFilters = {
      monthFilter: 'thisMonth',
      month: (now.getMonth() + 1).toString(),
      year: now.getFullYear().toString(),
      otherMonth: null,
      category: 'all',
      executive: 'all'
    };
    setSelectedFilters(resetFilters);
    setSearchTerm('');
    fetchDataForMonth(now.getFullYear(), now.getMonth() + 1);
  };

  //Function to handle filter button clicks
  const handleFilterChange = (key, val) => {
    if (key === 'monthFilter') {
      if (val === 'thisMonth') {
        const now = new Date();
        setSelectedFilters((prev) => ({
          ...prev,
          monthFilter: val,
          otherMonth: null,
          month: (now.getMonth() + 1).toString(),
          year: now.getFullYear().toString()
        }));
        // Fetch with current category filter
        fetchDataForMonth(now.getFullYear(), now.getMonth() + 1, selectedFilters.category);
      } else if (val === 'lastMonth') {
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        setSelectedFilters((prev) => ({
          ...prev,
          monthFilter: val,
          otherMonth: null,
          month: (lastMonth.getMonth() + 1).toString(),
          year: lastMonth.getFullYear().toString()
        }));
        // Fetch with current category filter
        fetchDataForMonth(lastMonth.getFullYear(), lastMonth.getMonth() + 1, selectedFilters.category);
      } else if (val === 'other') {
        setSelectedFilters((prev) => ({
          ...prev,
          monthFilter: val
        }));
        // Don't fetch yet, wait for otherMonth selection
      }
    } else if (key === 'otherMonth') {
      const [shortMonthName, year] = val.split(" ");
      const date = parse(shortMonthName, "MMM", new Date());
      const monthIndex = getMonth(date) + 1;
      setSelectedFilters((prev) => ({
        ...prev,
        otherMonth: val,
        year: year,
        month: monthIndex.toString()
      }));
      // Fetch with current category filter
      fetchDataForMonth(parseInt(year), monthIndex, selectedFilters.category);
    } else {
      setSelectedFilters((prev) => ({ ...prev, [key]: val }));
    }

    if (key === 'category') {
      setSelectedFilters((prev) => ({ ...prev, category: val }));
      // Refetch with current month/year and new category
      if (selectedFilters.year && selectedFilters.month) {
        fetchDataForMonth(parseInt(selectedFilters.year), parseInt(selectedFilters.month));
      }
    }
  }

  const sortData = (key) => {
    let direction = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })

    const sorted = [...expenses].sort((a, b) => {
      let aValue = a[key] || ''
      let bValue = b[key] || ''

      if (key === 'amount') {
        aValue = Number(aValue) || 0
        bValue = Number(bValue) || 0
        return direction === 'asc' ? aValue - bValue : bValue - aValue
      }

      if (key === 'date') {
        const parse = (str) => {
          if (!str) return 0;
          return new Date(str);
        };
        const aDate = new Date(a.expenseDT);
        const bDate = new Date(b.expenseDT);

        return direction === 'asc'
          ? aDate - bDate
          : bDate - aDate;
      }

      return 0
    })

    setExpenses(sorted)
  }

  // Expense Stats Component
  const ExpenseStats = ({ expenses }) => {
    const categoryColors = ["#094418", "#0b5d1e", "#107a28", "#17a53b"];

    // 4 categories stats calculation and card rendering
    const categoryStats = useMemo(() => {
      if (!Array.isArray(expenses)) return [];

      return expenseTypesArray.map((expenseType, index) => {
        const total = expenses
          .filter(exp => exp.expenseTypeID === expenseType.ID)
          .reduce((sum, exp) => sum + exp.amount, 0);

        return {
          category: expenseType.name,
          total,
          color: categoryColors[index]
        };
      });
    }, [expenses])

    return (
      <div className="w-full">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          {categoryStats.map((stat, index) => (
            <div
              key={stat.category}
              className="relative px-3 sm:px-5 py-3 sm:py-4 rounded-md transition-all text-white flex flex-col justify-start flex-1"
              style={{
                backgroundColor: stat.color,
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <div className="flex justify-between sm:flex-col gap-1">
                {/* Category Name */}
                <div className="">
                  <span className="text-sm sm:text-lg font-medium">{stat.category}</span>
                </div>

                {/* Total Amount */}
                <div className="justify-start flex items-center">
                  <span className="font-bold text-xl sm:text-2xl md:text-3xl">
                    <FormatAmount amount={stat.total} currency={currencySymbol} showCurr={true} showDecimalPoints={0} />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Mobile view Card component
  const MobileCard = ({ entry }) => {

    return (
      <div className="bg-sahi-white sahi-radius shadow-sm border border-gray-200 mb-2 w-full f-col justify-between">
        {/* Header Section */}
        <div className="f-col mobile-tile-header-layout gap-1">
          <div className="f-row justify-between items-center">
            <div className="flex items-center gap-2 text-text-color">
              <span className="text-text-color text-xs">
                {expenseTypesArray.find(type => type.ID === entry.expenseTypeID)?.name || '-'}
              </span>
            </div>
            <div className="f-col items-end text-md font-semibold">
              <div className="f-row gap-1 items-center justify-end">
                <span className={`text-text-color text-xs`}>
                  {getTableDateOrWeekday(entry.expenseDT, false, true) || '-'}
                </span>
              </div>
            </div>
          </div>
          <div className="f-row justify-between items-center">
            <div className="flex items-center gap-2 mt-1 text-text-color">
              <div className="f-row gap-1 text-lg items-center justify-end">
                <span className="whitespace-nowdrap text-gray-500 text-base text-xs">
                  {currencySymbol}
                </span>
                <span className="text-text-color font-semibold text-xs">
                  <FormatAmount
                    amount={entry.amount}
                    currency={currencySymbol}
                    showCurr={false}
                    showDecimalPoints={2}
                  />
                </span>
              </div>
            </div>
            <div className="f-col items-end text-md font-semibold">

            </div>
          </div>
          <div className={`overflow-hidden transition-all duration-300 ease-in-out max-h-96 opacity-100`}>
            <div className="text-xs text-text-color break-words text-justify">
              {entry.notes}
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div onClick={() => handleEditExpense(entry)}
          className="flex justify-between items-center text-xs border-t border-[#f1f1f1] w-full mobile-tile-footer-layout">
          {entry.userID ? (<div className="flex items-center gap-2 text-text-color">
            <div className="f-row gap-1 text-sm items-center justify-end">
              <User size={14} />
              <span className="text-text-color text-xs">
                {getExecutiveName(entry.userID)}
              </span>
            </div>
          </div>) : <div></div>}
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEditExpense(entry)}
              className="h-4 w-4 p-0 flex items-center justify-center hover:shadow-sm transform-gpu transition-colors duration-200 ease-out hover:scale-110 active:scale-95"
            >
              <Pencil className="h-3 w-3 text-sahi-blue" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-sahi-primary-color mr-3" />
        <span className="text-text-color">Loading</span>
      </div>
    );
  }

  return (
    <TooltipProvider>
      {/* Header */}
      {/* Desktop Header */}
      <div className="hidden lg:block">
        <div className="flex justify-between items-center gap-3">
          <div className="flex items-center flex-grow">
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-text-color mr-4 web-header-name">Expense Records</h1>
            </div>
          </div>
          <ExpenseEntry
            triggerButton={true}
            triggerText="Enter Expense"
            defaultMonth={parseInt(selectedFilters.month)}
            defaultYear={parseInt(selectedFilters.year)}
            onExpenseSet={() => {
              fetchDataForMonth(parseInt(selectedFilters.year), parseInt(selectedFilters.month));
            }}
          />
        </div>
      </div>

      {/* Mobile Header */}
      <div className="flex flex-col lg:hidden gap-3 mt-3">
        <div className="w-full flex items-center gap-2">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
            <Input
              value={searchTerm}
              placeholder="Search expenses"
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 w-full"
            />
          </div>
          <FilterButton />
        </div>
      </div>

      <div className="space-y-6 overflow-x-hidden mt-5">
        {/* Display Filters */}
        {!isMobile && <div className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4">
                {filterOptions.map(({ key, label, options, displayValue, disabled, searchEnabled, showClearButton, hideAllOption, mobileDropdownText, type }) => {
                  return (
                    <CommonFilter
                      key={key}
                      label={label}
                      options={options}
                      value={selectedFilters[key]}
                      onChange={(val) => handleFilterChange(key, val)}
                      displayValue={displayValue}
                      disabled={disabled}
                      searchEnabled={searchEnabled}
                      showClearButton={showClearButton}
                      hideAllOption={hideAllOption}
                      mobileDropdownText={mobileDropdownText}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>}

        {/* Mobile Filter Drawer */}
        {isFilterSelected && isMobile && (
          <Drawer open={isDrawerOpen} onOpenChange={(open) => {
            setIsDrawerOpen(open);
            if (!open) {
              setIsFilterSelected(false);
            }
          }}>
            <DrawerContent className="p-0 [&>div:first-child]:hidden">
              <div className="flex flex-col h-[100dvh]">
                <div className="flex items-center justify-between p-4 border-b-2 border-gray-200">
                  <div className="flex gap-4 items-center ">
                    <DrawerClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                      <ArrowLeft className="h-[24px] w-[24px]" onClick={() => {
                        setIsDrawerOpen(false);
                        setIsFilterSelected(false);
                      }} />
                      <span className="sr-only">Back</span>
                    </DrawerClose>
                    <p className="text-lg font-semibold text-sahi-primary-color">Expense Filters</p>
                  </div>
                  <button onClick={handleResetFilters} className="group flex items-center gap-1 text-sm font-light hover:underline text-sahi-primary-color">
                    <RiResetLeftFill className="group-hover:rotate-[-20deg] transition-transform duration-200" />
                    <span>Reset</span>
                  </button>
                </div>
                <div className="flex flex-1 overflow-hidden">
                  <div className="flex-1 p-4 overflow-y-auto filter-scroll-container">
                    {filterOptions.map(({ key, label, options, displayValue, type, disabled, searchEnabled, mobileDropdownText }) => (
                      <div key={key} id={key} className="mb-4">
                        <DrawerFilter
                          id={key}
                          label={label}
                          options={options}
                          value={selectedFilters[key]}
                          displayValue={displayValue}
                          onChange={(value) => handleFilterChange(key, value)}
                          type={type}
                          disabled={disabled}
                          mobileDropdownText={mobileDropdownText}
                          searchEnabled={searchEnabled}
                          isSorting={false}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <DrawerFooter className="sticky bottom-0 border-t-2 p-3 bg-white">
                  <DrawerClose>
                    <div className="flex justify-start gap-2">
                      <Button
                        className="sahi-primary-button"
                        onClick={() => {
                          setIsDrawerOpen(false);
                          setSelectedFilters(selectedFilters);
                        }}
                      >
                        Apply
                      </Button>
                      <Button variant="outline" className="sahi-secondary-button" onClick={() => {
                        setIsDrawerOpen(false);
                        setIsFilterSelected(false);
                      }}>
                        Cancel
                      </Button>
                    </div>
                  </DrawerClose>
                </DrawerFooter>
              </div>
            </DrawerContent>
          </Drawer>
        )}

        <div>
          <ExpenseStats expenses={expenses} />
        </div>

        {/* Amount Entries Table */}
        {isLoading ? (
          <div className="flex justify-center items-center w-full h-full">
            <Loader2 className="h-8 w-8 animate-spin text-sahi-primary-color mr-3" />
            <span className="text-text-color">Loading</span>
          </div>
        ) : filteredExpenses.length > 0 ? (
          isMobile ? (
            <div>
              {/* Mobile View - List */}
              {filteredExpenses.map((entry) => (
                <div key={entry.id}>
                  <MobileCard entry={entry} />
                </div>
              ))}
              {filteredExpenses.length <= 4 && (
                <div className="mt-2">
                  <ExpenseEntry
                    triggerButton={true}
                    triggerText="Enter Expense"
                    defaultMonth={parseInt(selectedFilters.month)}
                    defaultYear={parseInt(selectedFilters.year)}
                    onExpenseSet={() => {
                      fetchDataForMonth(parseInt(selectedFilters.year), parseInt(selectedFilters.month));
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <Card className="bg-sahi-white hidden lg:block !pt-0 overflow-hidden mt-5 max-w-full">
              <CardContent className="px--5 !sahi-radius">
                <Table className="min-w-full table-auto">
                  <TableHeader>
                    <TableRow className="bg-sahi-white py-1.5 h-11">
                      {tableHeaderCols.map(({ key, label, className = '', style = {} }) => (
                        <TableHead
                          key={key}
                          className="font-semibold text-sm text-black whitespace-nowrap uniform-spacing !pr-0"
                          style={style}
                          onClick={() => sortData(key)}
                        >
                          <div className={`flex items-center cursor-pointer ${className}`}>
                            {label}
                            {sortConfig.key === key && sortConfig.direction && (
                              sortConfig.direction === 'asc' ? (
                                <ArrowUp className="ml-1 h-4 w-4 text-black" />
                              ) : (
                                <ArrowDown className="ml-1 h-4 w-4 text-black" />
                              )
                            )}
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {filteredExpenses.map((entry) => (
                      <TableRow key={entry.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleEditExpense(entry)}>
                        <TableCell className="uniform-spacing pl-4 w-[280px]">
                          <span className="text-text-color">
                            {getTableDateOrWeekday(entry.expenseDT, false, true) || '-'}
                          </span>
                        </TableCell>

                        <TableCell className="uniform-spacing pl-4 w-[280px]">
                          <span className="text-text-color">
                            {expenseTypesArray.find(type => type.ID === entry.expenseTypeID)?.name || '-'}
                          </span>
                        </TableCell>

                        <TableCell className="text-right w-[95px] pr-0 uniform-spacing">
                          <span className="text-text-color">
                            <FormatAmount
                              amount={entry.amount}
                              currency={currencySymbol}
                              showCurr={false}
                              showDecimalPoints={entry.amount != Math.floor(entry.amount) ? 2 : 0}
                            />
                          </span>
                        </TableCell>

                        {selectedFilters.executive === 'all' && (
                          <TableCell className="uniform-spacing pl-4 w-[280px]">
                            <span className="text-text-color">
                              {getExecutiveName(entry.userID) || '-'}
                            </span>
                          </TableCell>)}

                        <TableCell className="uniform-spacing pl-4 max-w-[300px]">
                          <div className="flex flex-col gap-1">
                            <span className="text-text-color font-medium">
                              {entry.notes?.trim() || '-'}
                            </span>
                          </div>
                        </TableCell>

                        <TableCell className="text-right flex justify-end items-center px-2 pr-4">
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditExpense(entry)}
                              className="h-6 w-6 p-0 flex items-center justify-center hover:shadow-sm transform-gpu transition-colors duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-blue-light"
                            >
                              <Pencil className="h-4 w-4 text-sahi-blue" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )
        ) : (
          <div className="flex flex-col items-start gap-5">
            <div className="no-records border border-gray-200 shadow-sm sahi-radius">
              No expenses found.
            </div>
            <ExpenseEntry
              triggerButton
              triggerText="Enter Expense"
              defaultMonth={parseInt(selectedFilters.month)}
              defaultYear={parseInt(selectedFilters.year)}
              onExpenseSet={() => {
                fetchDataForMonth(parseInt(selectedFilters.year), parseInt(selectedFilters.month));
              }}
            />
          </div>
        )}

        {editingExpense && (
          <ExpenseEntry
            dialogTitle="Edit Expense"
            triggerButton={false}
            open={isEditDialogOpen}
            onOpenChange={(open) => {
              setIsEditDialogOpen(open);
              if (!open) {
                setEditingExpense(null);
              }
            }}
            prefilledData={editingExpense}
            defaultMonth={parseInt(selectedFilters.month)}
            defaultYear={parseInt(selectedFilters.year)}
            onExpenseSet={() => {
              fetchDataForMonth(parseInt(selectedFilters.year), parseInt(selectedFilters.month));
            }}
          />
        )}
      </div>
    </TooltipProvider>
  )
}

export default Expenses